import React, { useCallback, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';

import { CarouselLeftSmall, CarouselRightSmall } from '@rover/icons';
import { Box, Button, Flex, Image } from '@rover/kibble/core';
import { SrcsetArray } from '@rover/kibble/core/Image';
import { GalleryViewMode } from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import useDebounce from '@rover/react-lib/src/hooks/useDebounce';
import {
  MaximalCardEventTags,
  SearchResultGalleryClickEvent,
  SearchResultGalleryScrollEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import { shouldLazyLoadImage } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { CardGalleryImage, CardGalleryImages } from '@rover/types/src/CardGalleryImages';

import MaximalCardImagesContainer from './MaximalCardImagesContainer';

type Props = {
  images: CardGalleryImages | null;
  imageKey: string;
  index: number | undefined;
  avatarUrl: string;
  analyticsProps: MaximalCardEventTags;
  providerOpk: string;
  providerName: string;
  profileUrl: string;
  onProfileClick?: () => void;
  isDesktop?: boolean;
  isSeoPage?: boolean;
  isV2?: boolean;
};

// V2 condenses the card with smaller gallery photos; V1 keeps the original size.
const IMAGE_SIZE_V1 = 120;
const IMAGE_SIZE_V2 = 80;

const IMAGE_GAP = 8;

const MaximalCardGallery = ({
  images,
  imageKey,
  index: cardIndex,
  avatarUrl,
  analyticsProps,
  providerOpk,
  providerName,
  profileUrl,
  onProfileClick,
  isDesktop,
  isSeoPage,
  isV2,
}: Props): JSX.Element | null => {
  const IMAGE_SIZE = isV2 ? IMAGE_SIZE_V2 : IMAGE_SIZE_V1;
  const hrefTarget = isDesktop && !isSeoPage ? '_blank' : '_self';
  const imageSizeString = `${IMAGE_SIZE}`;
  const srcSetWidths: SrcsetArray = [imageSizeString, imageSizeString, imageSizeString];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showShiftLeft, setShowShiftLeft] = useState(false);
  const [showShiftRight, setShowShiftRight] = useState(true);
  const [hovering, setHovering] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState<GalleryViewMode>(GalleryViewMode.Lightbox);
  const [selectedGalleryImageIndex, setSelectedGalleryImageIndex] = useState(0);

  const emitScrollEvent = useCallback(() => {
    emitAnalyticsEvent(new SearchResultGalleryScrollEvent(analyticsProps));
  }, [analyticsProps]);

  const { start: debouncedEmitScroll } = useDebounce(emitScrollEvent, 300);

  const emitClickEvent = useCallback(() => {
    emitAnalyticsEvent(new SearchResultGalleryClickEvent(analyticsProps));
  }, [analyticsProps]);

  const handleGalleryImageClick = (index: number): void => {
    if (!isGalleryOpen) setIsGalleryOpen(true);
    setSelectedGalleryImageIndex(index);
    setGalleryViewMode(GalleryViewMode.Lightbox);
  };

  const handleViewGallery = (): void => {
    setGalleryViewMode(GalleryViewMode.Grid);
  };

  const handleViewAll = (): void => {
    setGalleryViewMode(GalleryViewMode.Grid);
    setIsGalleryOpen(true);
  };

  const shift = (direction: number): void => {
    if (!containerRef.current) return;
    const { clientWidth, scrollLeft } = containerRef.current;
    const newScrollLocation = scrollLeft + clientWidth * direction;
    containerRef.current.scrollTo({ behavior: 'smooth', left: newScrollLocation });
  };

  const shiftRight = (): void => {
    shift(1);
    emitClickEvent();
  };

  const shiftLeft = (): void => {
    shift(-1);
    emitClickEvent();
  };

  const onScroll = (): void => {
    if (!containerRef.current) return;
    const { scrollWidth, clientWidth, scrollLeft } = containerRef.current;
    setCurrentIndex(Math.round(scrollLeft / (IMAGE_SIZE + IMAGE_GAP)));
    if (scrollLeft > 0) {
      setShowShiftLeft(true);
      if (5 + scrollLeft >= scrollWidth - clientWidth) {
        setShowShiftRight(false);
      } else {
        setShowShiftRight(true);
      }
    } else {
      setShowShiftLeft(false);
    }
    debouncedEmitScroll();
  };

  const imagesToShow = [
    { smallImage: avatarUrl, index: 0, largeUncropped: avatarUrl } as CardGalleryImage,
  ];
  if (images?.galleryImages)
    images.galleryImages.forEach((cardImage) => {
      imagesToShow.push({ ...cardImage, index: cardImage.index + 1 });
    });

  const hideControls = imagesToShow.length < 3;

  return (
    <Box
      ref={containerRef}
      webProps={{
        onScroll,
        onMouseEnter: () => !hovering && setHovering(true),
        onMouseLeave: () => hovering && setHovering(false),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'ArrowRight') {
            shiftRight();
            e.preventDefault();
          }
          if (e.key === 'ArrowLeft') {
            shiftLeft();
            e.preventDefault();
          }
        },
        tabIndex: 0,
        role: 'region',
      }}
      height={`${IMAGE_SIZE}px`}
      display="flex"
      mt="2x"
      maxWidth="calc(100% + 24px)"
      marginRight="-4x"
      paddingRight="3x"
      sx={{
        zIndex: 1,
        columnGap: `${IMAGE_GAP}px`,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
      aria-roledescription="carousel"
    >
      {imagesToShow.map(({ smallImage }, index) => {
        const ariaIndex = index + 1;
        const image = (
          <Image
            height={IMAGE_SIZE}
            width={IMAGE_SIZE}
            webProps={{
              src: smallImage,
              srcsetWidths: srcSetWidths,
              sizes: srcSetWidths.map((size) => `${size}px`),
              eager: !shouldLazyLoadImage(cardIndex),
            }}
            resizeMethod="cover"
            description=""
            aria-hidden
            testID="maximal-gallery-image"
            borderRadius="primary"
            sx={{
              minWidth: IMAGE_SIZE,
              objectFit: 'cover',
              overflow: 'clip',
            }}
          />
        );
        if (imagesToShow.length === index + 1 && !hideControls && !isV2) {
          return (
            <Flex
              key={`Last image ${index}, ${imageKey}`}
              position="relative"
              display="flex"
              flexDirection="column"
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
              justifyContent="center"
              minHeight={IMAGE_SIZE}
              minWidth={IMAGE_SIZE}
              sx={{ scrollSnapAlign: 'start' }}
            >
              {!showShiftRight && (
                <Button
                  size="small"
                  sx={{ zIndex: 2, padding: '6px 14px' }}
                  onPress={handleViewAll}
                  aria-label={t`View all photos for ${providerName}.`}
                >{t`View all`}</Button>
              )}
              <Flex position="absolute">
                <Flex
                  borderRadius="primary"
                  position="absolute"
                  width="100%"
                  height="100%"
                  background="overlay"
                  minHeight="100%"
                  minWidth="100%"
                />
                {image}
              </Flex>
            </Flex>
          );
        }
        return (
          <Button
            p="0x"
            href={profileUrl}
            webProps={{ hrefTarget, ...(isSeoPage ? { rel: 'nofollow' } : {}) }}
            aria-label={t`View ${providerName}'s profile, photo ${ariaIndex}`}
            onPress={() => onProfileClick?.()}
            key={`image ${index}, ${imageKey}`}
            display="flex"
            flexDirection="column"
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            justifyContent="center"
            minHeight={IMAGE_SIZE}
            minWidth={IMAGE_SIZE}
            sx={{ scrollSnapAlign: 'start', border: 'none', borderRadius: '0', outline: 'none' }}
          >
            {image}
          </Button>
        );
      })}
      {isV2 && !hideControls && (
        <Flex
          key={`view-all-${imageKey}`}
          flexShrink="0"
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          minWidth={IMAGE_SIZE}
          minHeight={IMAGE_SIZE}
          justifyContent="center"
          alignItems="center"
          background="primary"
          borderRadius="primary"
          sx={{ scrollSnapAlign: 'start' }}
        >
          <Button
            px="0x"
            width={IMAGE_SIZE}
            height={34}
            size="small"
            variant="default"
            onPress={handleViewAll}
            aria-label={t`View all photos for ${providerName}.`}
          >{t`View all`}</Button>
        </Flex>
      )}
      {!hideControls && hovering && showShiftLeft && (
        <Button
          display={['none', 'flex', 'flex']}
          pl="0x"
          pr="0x"
          width="8x"
          height="8x"
          sx={{ zIndex: 2, position: 'absolute', left: '6x', justifyContent: 'center' }}
          circular
          onPress={shiftLeft}
          icon={CarouselLeftSmall}
          iconAlignment="right"
          aria-label={t`Previous photo`}
        />
      )}
      {!hideControls && hovering && showShiftRight && (
        <Button
          display={['none', 'flex', 'flex']}
          pl="0x"
          pr="0x"
          width="8x"
          height="8x"
          sx={{ zIndex: 2, position: 'absolute', right: '6x', justifyContent: 'center' }}
          circular
          onPress={shiftRight}
          icon={CarouselRightSmall}
          iconAlignment="right"
          aria-label={t`Next photo`}
        />
      )}
      <Box
        aria-live="polite"
        aria-atomic="true"
        position="absolute"
        sx={{
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
        }}
      >
        {(() => {
          const photoNumber = currentIndex + 1;
          const totalPhotos = imagesToShow.length;
          return t`Photo ${photoNumber} of ${totalPhotos}`;
        })()}
      </Box>
      {isGalleryOpen && (
        <MaximalCardImagesContainer
          profileImage={imagesToShow[0]}
          providerOpk={providerOpk}
          galleryViewMode={galleryViewMode}
          onClose={() => setIsGalleryOpen(false)}
          onImageClick={handleGalleryImageClick}
          onViewGallery={handleViewGallery}
          setLightboxIndex={setSelectedGalleryImageIndex}
          lightboxIndex={selectedGalleryImageIndex}
          initialTotalImageCount={(images?.imageCount ?? 0) + 1}
        />
      )}
    </Box>
  );
};
export default MaximalCardGallery;
